/**
 * FloatingToolbar.tsx
 * Bottom-anchored presenter toolbar with all drawing and class tools.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Pen, Highlighter, Zap, Eraser, Square, Globe,
  Users, BarChart2, Trash2, Undo2, ChevronUp, ChevronDown,
} from 'lucide-react';
import { clsx } from 'clsx';
import { usePresenterStore, type DrawingTool } from '@store/presenterStore';

interface ToolBtn {
  tool?: DrawingTool;
  icon: React.ReactNode;
  label: string;
  action?: () => void;
  color?: string;
  divider?: boolean;
}

export default function FloatingToolbar(): React.ReactElement {
  const {
    activeTool, setActiveTool,
    setWhiteboardOpen, whiteboardOpen,
    setBrowserOpen, browserOpen,
    setNamePickerOpen, namePickerOpen,
    setResultsOpen, resultsOpen,
    undoCanvas, clearCanvas,
  } = usePresenterStore();

  const [collapsed, setCollapsed] = useState(false);

  const toggleTool = (tool: DrawingTool) => {
    setActiveTool(activeTool === tool ? 'none' : tool);
  };

  const TOOLS: ToolBtn[] = [
    { tool: 'pen',         icon: <Pen size={16} />,         label: 'Pen',         color: 'text-red-400' },
    { tool: 'highlighter', icon: <Highlighter size={16} />, label: 'Highlight',   color: 'text-yellow-400' },
    { tool: 'laser',       icon: <Zap size={16} />,         label: 'Laser',       color: 'text-red-500' },
    { tool: 'eraser',      icon: <Eraser size={16} />,      label: 'Eraser',      color: 'text-slate-600' },
    { divider: true, icon: null, label: '' },
    { icon: <Undo2 size={16} />,  label: 'Undo',      action: () => undoCanvas(),   color: 'text-slate-600' },
    { icon: <Trash2 size={16} />, label: 'Clear',     action: () => clearCanvas(),  color: 'text-slate-500' },
    { divider: true, icon: null, label: '' },
    { icon: <Square size={16} />, label: 'Whiteboard', action: () => { setWhiteboardOpen(!whiteboardOpen); }, color: whiteboardOpen ? 'text-brand-400' : 'text-slate-600' },
    { icon: <Globe size={16} />,  label: 'Browser',    action: () => { setBrowserOpen(!browserOpen); }, color: browserOpen ? 'text-brand-400' : 'text-slate-600' },
    { icon: <Users size={16} />,  label: 'Names',      action: () => { setNamePickerOpen(!namePickerOpen); }, color: namePickerOpen ? 'text-brand-400' : 'text-slate-600' },
    { icon: <BarChart2 size={16} />, label: 'Results', action: () => { setResultsOpen(!resultsOpen); }, color: resultsOpen ? 'text-green-400' : 'text-slate-600' },
  ];

  return (
    <motion.div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9050]"
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30, delay: 0.2 }}
    >
      {/* Collapse toggle */}
      <div className="flex justify-center mb-2">
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="glass rounded-full p-1.5 text-slate-500 hover:text-slate-900 transition-colors pointer-events-auto"
        >
          {collapsed ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 16 }}
            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
            className="glass rounded-2xl px-4 py-3 flex items-center gap-1 shadow-glass"
          >
            {TOOLS.map((btn, i) => {
              if (btn.divider) {
                return <div key={`div-${i}`} className="w-px h-8 bg-surface-border mx-1.5" />;
              }

              const isActive = btn.tool && activeTool === btn.tool;
              return (
                <motion.button
                  key={btn.label}
                  whileHover={{ scale: 1.12 }}
                  whileTap={{ scale: 0.92 }}
                  title={btn.label}
                  onClick={btn.action ?? (btn.tool ? () => toggleTool(btn.tool!) : undefined)}
                  className={clsx(
                    'flex flex-col items-center gap-0.5 p-2 rounded-xl transition-all min-w-[44px]',
                    isActive
                      ? 'bg-brand-500/25 border border-brand-500/40'
                      : 'hover:bg-surface-elevated',
                    btn.color ?? 'text-slate-600',
                  )}
                >
                  {btn.icon}
                  <span className="text-[9px] font-medium text-slate-500">{btn.label}</span>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
